import { configure } from 'mobx';

import NetworkMonitor from 'Services/network-monitor';
import { storeTokens } from 'Services/oauth';

import RootStore from 'Stores';

configure({ enforceActions: 'observed' });

const setStorageEvents = root_store => {
    window.addEventListener('storage', evt => {
        switch (evt.key) {
            case 'client.accounts': {
                const active_loginid = root_store.client.loginid;
                const new_currency = JSON.parse(evt.newValue)?.[active_loginid]?.currency;
                const old_currency = JSON.parse(evt.oldValue)?.[active_loginid]?.currency;

                if (document.hidden && new_currency && old_currency !== new_currency) {
                    root_store.client.updateAccountCurrency(new_currency, false);
                }
                break;
            }
            case 'active_loginid':
                if (localStorage.getItem('active_loginid') === 'null' || !localStorage.getItem('active_loginid')) {
                    root_store.client.logout();
                }
                if (document.hidden && window === window.top) {
                    window.location.reload();
                }
                break;
            // Cross-tab logout sentinel: logout.js writes this key on logout
            case 'logout_event':
                if (evt.newValue) {
                    root_store.client.logout();
                }
                break;
            // no default
        }
    });
};

/**
 * postMessage bridge for the parent app (newapiOnly).
 * Accepts ACCOUNT_CHANGED and LOGOUT messages — enables session sync
 * when dtrader-template is embedded as an iframe in newapiOnly.
 */
const setupParentMessageBridge = root_store => {
    // Same-origin embed: parent and iframe share `window.location.origin`,
    // so we trust it directly. No per-env list to maintain.
    const TRUSTED_ORIGINS = [window.location.origin];

    // Mutex: prevent concurrent ACCOUNT_CHANGED handling.
    // newapiOnly sends duplicate messages (DTRADER_READY + handleLoad retries at 1s/3s/5s).
    // Each one would call closeAndOpenNewConnection() — killing the previous WS mid-handshake
    // → "WebSocket closed before connection is established".
    let bridge_init_in_progress = false;

    window.addEventListener('message', async event => {
        if (!TRUSTED_ORIGINS.some(o => event.origin.startsWith(o.replace(/\/$/, '')))) return;

        const { type, newLoginId, authToken } = event.data ?? {};

        if (type === 'ACCOUNT_CHANGED' && authToken && newLoginId) {
            // Skip duplicate ACCOUNT_CHANGED while auth is already in flight.
            // Allow through if already authorized with a DIFFERENT loginid (legitimate account switch).
            const is_account_switch = root_store.client.is_authorize && root_store.client.loginid !== newLoginId;
            if (bridge_init_in_progress && !is_account_switch) return;

            bridge_init_in_progress = true;
            try {
                // authToken is an OAuth2 access token — store it and init.
                storeTokens(authToken);
                localStorage.setItem('active_loginid', newLoginId);

                if (root_store.client.is_authorize && root_store.client.loginid !== newLoginId) {
                    // Account switch: use switchAccount so sessionStorage and store loginid
                    // are updated BEFORE the OTP fetch, matching what init() expects.
                    // COMPONENTS_READY is sent by socket-general.js when the new connection's
                    // balance arrives — do NOT send it here so a failed switch doesn't
                    // prematurely unblock the parent's loading overlay.
                    await root_store.client.switchAccount(newLoginId);
                } else if (!root_store.client.is_authorize) {
                    // Sync sessionStorage before init() picks the active account.
                    sessionStorage.setItem('active_loginid', newLoginId);
                    await root_store.client.init();

                    // Fallback: if WS auth (balance path) didn't send COMPONENTS_READY
                    // (e.g. OTP WS never sent balance), unblock the parent loading overlay.
                    if (window !== window.top) {
                        window.parent.postMessage({ type: 'COMPONENTS_READY' }, '*');
                    }
                }
            } finally {
                bridge_init_in_progress = false;
            }
        }

        if (type === 'LOGOUT') {
            // Mid-session logout: tearing down the authenticated WS and
            // rebuilding a public one in-place leaves the chart's
            // subscriptions orphaned (the new socket fires `reconnect_handlers`
            // only when is_connected_before is true, which setWSUrl resets to
            // false) — so even after the new socket opens, ticks/active_symbols
            // never re-subscribe and the chart freezes.
            //
            // A hard iframe reload sidesteps the whole transition. After reload,
            // initStore takes the iframe-with-no-parent-auth branch → calls
            // init() → opens the public new-API WS cleanly. Same code path as
            // visiting the iframe URL while logged out, which the user
            // confirmed works.
            try {
                root_store.client.setLogout(true);
            } catch {
                /* ignore — mid-tear-down */
            }
            window.location.reload();
            return;
        }
    });

    // Tell the parent we are ready to receive auth data.
    // Also set is_logging_in so route guards don't block renders while waiting.
    if (window !== window.top) {
        root_store.client.setIsLoggingIn(true);
    }
    window.parent.postMessage({ type: 'DTRADER_READY' }, '*');
};

const initStore = async notification_messages => {
    const url_query_string = window.location.search;
    const url_params = new URLSearchParams(url_query_string);

    if (url_params.get('action') === 'signup') {
        // If a user comes from the signup process, give them a clean setup
        const server_url = localStorage.getItem('config.server_url');
        localStorage.clear();
        if (server_url) localStorage.setItem('config.server_url', server_url);
    }

    const root_store = new RootStore();

    // Set up global store reference for debugging
    if (typeof window !== 'undefined') {
        window.__deriv_store = root_store;
    }

    setStorageEvents(root_store);
    setupParentMessageBridge(root_store);

    NetworkMonitor.init(root_store);

    if (window === window.top) {
        // Standalone mode: run normal auth init
        root_store.client.init();
    } else {
        // Iframe mode. Two sub-cases:
        //  (a) Parent has an active session — wait for ACCOUNT_CHANGED to push
        //      us the auth token. Mark is_logging_in so route guards hold off.
        //  (b) Parent has no auth (user not logged in, or just logged out and
        //      the iframe reloaded) — we should NOT block forever waiting for
        //      a postMessage that's never coming. Run init() so the chart can
        //      load in public/anonymous mode against the new-API public WS.
        const parent_has_auth = !!localStorage.getItem('active_loginid');
        if (parent_has_auth) {
            root_store.client.setIsLoggingIn(true);
        } else {
            root_store.client.init();
        }
    }

    root_store.common.init();
    root_store.ui.init(notification_messages);

    return root_store;
};

export default initStore;
